import { useCallback, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus, QrCode, ShoppingCart } from 'lucide-react'

import type { SalesOrder, SalesOrderStatus } from '@/lib/api/sales-order-api'
import type { OrderListValues } from '@/components/orders/orders-list-filters'
import type { OrderListFilterSurface } from '@/components/orders/orders-list-filter-tracking'
import type { OrderListSurface } from '@/lib/order-list-tracking'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterSidebarLayout } from '@/components/common/list-filter-sidebar-layout'
import { OrdersExportDialog } from '@/components/orders/orders-export-dialog'
import { OrdersFilterPanel } from '@/components/orders/orders-filter-panel'
import {
    captureOrderListFiltersChanged,
    captureOrderListSidebarToggled,
} from '@/components/orders/orders-list-filter-tracking'
import { CARRIER_FILTER_QUERY } from '@/components/orders/orders-list-filter-options'
import {
    ORDERS_PER_PAGE,
    ORDER_LIST_FILTERS,
    describeOrderPeriod,
    orderListApiParams,
    searchLimitedByPeriod,
} from '@/components/orders/orders-list-filters'
import { OrdersListCards } from '@/components/orders/orders-list-cards'
import { OrdersListTable } from '@/components/orders/orders-list-table'
import { QRScannerDialog } from '@/components/sections/qr-scanner-dialog'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useClampPage } from '@/hooks/use-clamp-page'
import { useSalesOrders } from '@/hooks/use-sales-order-query'
import { useShippingCarriers } from '@/hooks/use-shipping-carriers-query'
import {
    LIST_SEARCH_KEYS,
    useRememberListSearch,
} from '@/lib/list-search-store'
import { captureOrderListResultOpened } from '@/lib/order-list-tracking'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

// A type alias (rather than an interface) remains assignable to TanStack's
// string/number search record while keeping this route's validated keys exact.
export type OrdersSearch = {
    q?: string
    // Khoảng nhanh theo ngày tạo; không có = Tháng này, có date_* = tùy chỉnh.
    period?: string
    // Một hoặc nhiều mã ngăn bởi dấu phẩy; `string & {}` vẫn nhận mã thô
    // backend mới thêm để link từ donut không bị vứt bộ lọc (FD-376).
    status?: SalesOrderStatus | (string & {})
    payment_status?: string
    einvoice_status?: string
    settlement_intent?: string
    sla_state?: string
    has_lot_debt?: 'owing'
    kiotviet_unlinked?: 'pending'
    // Chỉ đơn "Bán ngay" tại quầy — bảng nhắc hóa đơn mở (M-166).
    quick_sale?: 'only'
    source_channel?: string
    created_by?: 'me'
    shipping_carrier_id?: string
    date_from?: string
    date_to?: string
    delivered_from?: string
    delivered_to?: string
    // Trạng thái cột lọc desktop, KHÔNG phải bộ lọc: mặc định (mở rộng) không
    // ghi lên URL.
    filters?: 'collapsed'
    page?: number
}

/** Cột lọc desktop đang thu gọn (M-59). */
const FILTERS_COLLAPSED = 'collapsed'

const isFilterSidebarCollapsed = (search: Record<string, unknown>) =>
    search.filters === FILTERS_COLLAPSED

export const Route = createFileRoute('/_app/_authed/_sidebar/orders/')({
    component: OrdersPage,
    // Trang + toàn bộ filter đẩy lên search param → reload / share link / back
    // giữ nguyên trạng thái. Cũng hỗ trợ deep-link từ dashboard (?payment_status=…).
    validateSearch: (search: Record<string, unknown>) => {
        const out = ORDER_LIST_FILTERS.validateSearch(search)
        // Thu gọn là trạng thái giao diện: giữ trên URL để F5 không bung lại,
        // nhưng không đi qua config bộ lọc nên không vào badge, chip hay API.
        return isFilterSidebarCollapsed(search)
            ? { ...out, filters: FILTERS_COLLAPSED }
            : out
    },
})

export function OrdersPage() {
    const navigate = useNavigate({ from: '/orders/' })
    const urlSearch = Route.useSearch()
    useRememberListSearch(LIST_SEARCH_KEYS.orders, urlSearch)
    // Chip lọc hãng cần TÊN, URL chỉ có id → tra từ danh sách hãng (cùng cache
    // với ô lọc); chưa tra được thì không sinh chip.
    const { data: carriersData } = useShippingCarriers(CARRIER_FILTER_QUERY)
    const selectedCarrier = (carriersData?.data ?? []).find(
        (carrier) => carrier.id === urlSearch.shipping_carrier_id,
    )

    // Cột lọc thu gọn hay không nằm ngoài config bộ lọc, nên mỗi lần ghi
    // search params phải chủ động mang nó theo — nếu không, đổi một ô lọc là
    // cột lại tự bung ra.
    const sidebarCollapsed = isFilterSidebarCollapsed(urlSearch)

    const { values, barProps, page, setPage } = useListFilters({
        def: ORDER_LIST_FILTERS,
        search: urlSearch,
        onSearchChange: (next, opts) =>
            void navigate({
                search: () =>
                    sidebarCollapsed
                        ? { ...next, filters: FILTERS_COLLAPSED }
                        : next,
                replace: opts?.replace ?? false,
            }),
        chipLabels: {
            shipping_carrier_id: selectedCarrier
                ? `${m.order_carrier_filter_label()}: ${selectedCarrier.name}`
                : undefined,
        },
    })

    const [qrOpen, setQrOpen] = useState(false)
    const { hasPermission } = useAuth()

    // Bấm 3 ngạch ngang chỉ đổi trạng thái cột lọc: giữ nguyên bộ lọc và trang
    // đang xem. `replace` để mỗi lần thu/mở không nhét thêm một entry lịch sử —
    // bấm Back là muốn rời màn, không phải bung lại cột lọc.
    const setSidebarCollapsed = (next: boolean) => {
        captureOrderListSidebarToggled({ values, collapsed: next })
        void navigate({
            search: () => ({
                ...ORDER_LIST_FILTERS.toSearch(values),
                page: page > 1 ? page : undefined,
                filters: next ? FILTERS_COLLAPSED : undefined,
            }),
            replace: true,
        })
    }

    const applyFrom =
        (surface: OrderListFilterSurface) => (next: OrderListValues) => {
            captureOrderListFiltersChanged({
                before: values,
                after: next,
                surface,
            })
            barProps.onApply(next)
        }
    const pendingList = values.einvoice_status === 'needs_action'
    const bar = {
        ...barProps,
        onApply: applyFrom('sheet'),
        onClear: () => {
            captureOrderListFiltersChanged({
                before: values,
                after: ORDER_LIST_FILTERS.defaults,
                surface: 'clear',
            })
            barProps.onClear()
        },
        chips: barProps.chips.map((chip) => ({
            ...chip,
            onRemove: () => {
                const key = chip.key as keyof OrderListValues
                captureOrderListFiltersChanged({
                    before: values,
                    after: {
                        ...values,
                        [key]: ORDER_LIST_FILTERS.defaults[key],
                    },
                    surface: 'chip',
                })
                chip.onRemove()
            },
        })),
    }

    const { data, isLoading, isError } = useSalesOrders(
        orderListApiParams(values, page),
    )
    const orders = data?.data ?? []
    const meta = data?.meta
    // Trang trên URL có thể vượt quá số trang (link cũ, đơn vừa rời bộ lọc):
    // về trang cuối còn dữ liệu, giữ nguyên bộ lọc và trạng thái cột lọc.
    useClampPage(
        page,
        meta?.total_pages,
        useCallback(
            (last: number) =>
                void navigate({
                    search: () => ({
                        ...ORDER_LIST_FILTERS.toSearch(values),
                        page: last > 1 ? last : undefined,
                        filters: sidebarCollapsed
                            ? FILTERS_COLLAPSED
                            : undefined,
                    }),
                    replace: true,
                }),
            [navigate, values, sidebarCollapsed],
        ),
    )
    const trackOpened =
        (surface: OrderListSurface) => (order: SalesOrder, index: number) =>
            captureOrderListResultOpened({
                query: values.q,
                order,
                index,
                page,
                perPage: ORDERS_PER_PAGE,
                surface,
            })

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const isFiltered = barProps.activeCount > 0 || !!values.q
    // Mặc định là Tháng này: rỗng thường vì kỳ chứ không phải "chưa có đơn",
    // kể cả khi đang tìm — đơn cần tìm có thể tạo tháng trước.
    const emptyThisMonth = values.period === ORDER_LIST_FILTERS.defaults.period
    const widenToAllTime = (surface: OrderListFilterSurface) => () =>
        applyFrom(surface)({
            ...values,
            period: 'all',
            date_from: '',
            date_to: '',
        })
    // Rỗng ở kỳ mặc định thì trạng thái rỗng đã có nút mở rộng — không lặp lại.
    const showSearchWiden =
        searchLimitedByPeriod(values) &&
        !(emptyThisMonth && !isLoading && orders.length === 0)
    const emptyState = isError ? (
        <p className="py-8 text-center text-destructive">
            {m.common_error_load()}
        </p>
    ) : (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {emptyThisMonth
                    ? m.order_empty_this_month()
                    : isFiltered
                      ? m.list_empty_filtered()
                      : m.order_no_data()}
            </span>
            <div className="flex flex-wrap justify-center gap-2">
                {emptyThisMonth ? (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={widenToAllTime('empty_state')}
                    >
                        {m.order_view_all_time()}
                    </Button>
                ) : null}
                {isFiltered ? (
                    <ClearFilterButton active onClear={bar.onClear} />
                ) : null}
            </div>
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                icon={<ShoppingCart className="size-5 shrink-0" />}
                title={m.order_title()}
                primaryActions={
                    <>
                        {hasPermission('invoices.export') && (
                            <OrdersExportDialog />
                        )}
                        {hasPermission('sales_orders.read') && (
                            <Button
                                size="sm"
                                variant="outline"
                                className="w-full"
                                onClick={() => setQrOpen(true)}
                            >
                                <QrCode className="size-4" />
                                {m.nav_scan_qr()}
                            </Button>
                        )}
                        <Button asChild size="sm">
                            <Link to="/orders/new" className="w-full">
                                <Plus className="size-4" />
                                {m.order_create_btn()}
                            </Link>
                        </Button>
                    </>
                }
            />

            <ListFilterSidebarLayout
                bar={bar}
                collapsed={sidebarCollapsed}
                onCollapsedChange={setSidebarCollapsed}
                panel={(ctx) => <OrdersFilterPanel {...ctx} />}
                onSidebarApply={applyFrom('sidebar')}
            >
                {isError ? null : (
                    <p
                        data-testid="orders-list-summary"
                        className="text-sm text-muted-foreground"
                    >
                        {m.order_list_summary({
                            count: String(meta?.total ?? orders.length),
                            period: describeOrderPeriod(values),
                        })}
                        {meta?.totals ? (
                            <span className="md:hidden">
                                {' · '}
                                {formatVnd(
                                    Math.round(meta.totals.total_amount),
                                )}
                            </span>
                        ) : null}
                        {showSearchWiden ? (
                            <>
                                {' · '}
                                <Button
                                    variant="link"
                                    className="h-auto border-0 p-0 text-sm md:h-auto"
                                    onClick={widenToAllTime('search_widen')}
                                >
                                    {m.order_search_all_time()}
                                </Button>
                            </>
                        ) : null}
                    </p>
                )}
                {values.einvoice_status !== 'all' ? (
                    <p className="text-xs text-muted-foreground">
                        {m.einvoice_filter_hint()}
                    </p>
                ) : null}

                {/* Mobile: card list */}
                <div className="space-y-2 md:hidden">
                    <OrdersListCards
                        orders={orders}
                        isLoading={isLoading}
                        emptyState={emptyState}
                        pendingList={pendingList}
                        onTrackOpen={trackOpened('cards')}
                        onOpen={(order) =>
                            void navigate({
                                to: '/orders/$orderId',
                                params: { orderId: order.id },
                                search: pendingList
                                    ? { from: 'pending_list' }
                                    : undefined,
                            })
                        }
                    />
                </div>

                {/* Desktop: table */}
                <div className="hidden md:block rounded-md border overflow-x-auto">
                    <OrdersListTable
                        orders={orders}
                        isLoading={isLoading}
                        emptyState={emptyState}
                        totals={meta?.totals}
                        totalCount={meta?.total}
                        pendingList={pendingList}
                        onTrackOpen={trackOpened('table')}
                    />
                </div>

                {/* Pagination */}
                {meta && meta.total_pages > 1 && (
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>
                            {m.order_total_count({ count: String(meta.total) })}
                        </span>
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page === 1}
                                onClick={() => setPage(page - 1)}
                            >
                                {m.common_pagination_prev()}
                            </Button>
                            <span className="flex items-center px-2">
                                {page} / {meta.total_pages}
                            </span>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page >= meta.total_pages}
                                onClick={() => setPage(page + 1)}
                            >
                                {m.common_pagination_next()}
                            </Button>
                        </div>
                    </div>
                )}
            </ListFilterSidebarLayout>
            <QRScannerDialog
                open={qrOpen}
                onOpenChange={setQrOpen}
                mode="navigate"
            />
        </div>
    )
}
